import React from "react";
import classname from "classnames";

import { IconOld } from "@partials";

interface ButtonParentProps {
  template: "primary" | "secondary" | "tertiary";
  isLoading?: boolean;
  isDisabled?: boolean;
  type?: "button" | "submit";
  onClick?: () => void;
  href?: string;
  target?: TargetType;
  prefix?: IconName;
  suffix?: IconName;
  dataTestId?: string;
}

interface Props extends ButtonParentProps {
  title: string;
}

interface ButtonParentType {
  parentProps: ButtonParentProps;
  children: React.ReactNode;
}

const ButtonParent = ({ parentProps, children }: ButtonParentType): JSX.Element => {
  const {
    template,
    href,
    isDisabled,
    isLoading,
    onClick,
    target,
    type = "submit",
    prefix,
    suffix,
    dataTestId,
  } = parentProps;

  const classes = classname("btn", `btn--${template}`, {
    "btn--disabled": isDisabled,
    "btn--loading": isLoading,
    "btn--w-prefix": !!prefix,
    "btn--w-suffix": !!suffix,
  });

  const handleOnClick = (): void => {
    if (isDisabled || isLoading) return;

    onClick();
  };

  if (href) {
    return (
      <a className={classes} href={href} target={target} onClick={handleOnClick}>
        {children}
      </a>
    );
  }

  return (
    <button
      className={classes}
      disabled={isDisabled}
      // eslint-disable-next-line react/button-has-type
      type={type}
      onClick={handleOnClick}
      data-testid={dataTestId}
    >
      {children}
    </button>
  );
};

const Button: React.FC<Props> = ({ title, ...props }) => {
  return (
    <ButtonParent parentProps={props}>
      {props?.prefix && <IconOld name={props.prefix} className="btn__prefix" />}

      <span>{title}</span>

      {props?.suffix && <IconOld name={props.suffix} className="btn__suffix" />}
    </ButtonParent>
  );
};

export default Button;
